import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Alert } from "../ui/alert";
import { Spinner } from "../ui/spinner";
import { Badge } from "../ui/badge";
import { Shield } from "lucide-react";
import {
  Form,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from "../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { RiskService } from "../../services/riskService";
import { MetadataService } from "../../services/metadataService";
import ApplyControlToRiskFromRiskView from "./ApplyControlToRiskFromRiskView";

const EditRiskForm = ({
  open,
  onClose,
  risk,
  organizationId,
  businessUnitId,
  onRiskUpdated,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category_id: "",
    severity_id: "",
    likelihood_id: "",
  });
  const [categories, setCategories] = useState([]);
  const [severities, setSeverities] = useState([]);
  const [likelihoods, setLikelihoods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [showControlDialog, setShowControlDialog] = useState(false);

  const fetchMetadata = async () => {
    try {
      setLoading(true);

      const [categoriesData, severitiesData, likelihoodsData] =
        await Promise.all([
          MetadataService.getRiskCreationCategories(
            organizationId,
            getAccessTokenSilently
          ),
          MetadataService.getRiskCreationSeverities(
            organizationId,
            getAccessTokenSilently
          ),
          MetadataService.getRiskCreationLikelihoods(
            organizationId,
            getAccessTokenSilently
          ),
        ]);

      setCategories(categoriesData);
      setSeverities(severitiesData);
      setLikelihoods(likelihoodsData);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && organizationId && risk) {
      fetchMetadata();
      // Populate form with existing risk data
      setFormData({
        name: risk.name || "",
        description: risk.description || "",
        category_id: risk.category_id || "",
        severity_id: risk.severity_id || "",
        likelihood_id: risk.likelihood_id || "",
      });
    }
  }, [open, organizationId, risk, businessUnitId, getAccessTokenSilently]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Risk name is required");
      return;
    }

    if (!formData.description.trim()) {
      setError("Risk description is required");
      return;
    }

    if (!risk?.id) {
      setError("Risk ID is required");
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      await RiskService.updateRisk(risk.id, formData, getAccessTokenSilently);

      onRiskUpdated();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setFormData({
        name: "",
        description: "",
        category_id: "",
        severity_id: "",
        likelihood_id: "",
      });
      setError(null);
      onClose();
    }
  };

  const handleControlApplied = () => {
    onRiskUpdated();
  };

  const hasNoMetadata =
    !loading &&
    (categories.length === 0 ||
      severities.length === 0 ||
      likelihoods.length === 0);

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Risk</DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex justify-center items-center p-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <Form className="space-y-4">
              {error && <Alert variant="destructive">{error}</Alert>}

              {hasNoMetadata && (
                <Alert>
                  No metadata (risk types, severities, or likelihoods) is
                  defined for this organization. Please create the required
                  metadata first.
                </Alert>
              )}

              <FormItem>
                <FormLabel>Risk Name *</FormLabel>
                <FormControl>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="Enter a concise name for the risk..."
                    disabled={submitting}
                  />
                </FormControl>
                <FormDescription>
                  Provide a clear, concise name for the risk
                </FormDescription>
              </FormItem>

              <FormItem>
                <FormLabel>Risk Description *</FormLabel>
                <FormControl>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Describe the risk in detail..."
                    disabled={submitting}
                    rows={4}
                  />
                </FormControl>
                <FormDescription>
                  Provide a detailed description of the risk
                </FormDescription>
              </FormItem>

              <FormItem>
                <FormLabel>Category *</FormLabel>
                <FormControl>
                  <Select
                    value={formData.category_id}
                    onValueChange={(value) =>
                      handleInputChange("category_id", value)
                    }
                    disabled={submitting || categories.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                {categories.length === 0 && (
                  <FormMessage>
                    No categories available. Create categories first.
                  </FormMessage>
                )}
              </FormItem>

              <FormItem>
                <FormLabel>Severity *</FormLabel>
                <FormControl>
                  <Select
                    value={formData.severity_id}
                    onValueChange={(value) =>
                      handleInputChange("severity_id", value)
                    }
                    disabled={submitting || severities.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select severity level" />
                    </SelectTrigger>
                    <SelectContent>
                      {severities.map((severity) => (
                        <SelectItem key={severity.id} value={severity.id}>
                          {severity.name} (Level {severity.value})
                          {severity.description
                            ? ` - ${severity.description}`
                            : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                {severities.length === 0 && (
                  <FormMessage>
                    No severities available. Create severities first.
                  </FormMessage>
                )}
              </FormItem>

              <FormItem>
                <FormLabel>Likelihood *</FormLabel>
                <FormControl>
                  <Select
                    value={formData.likelihood_id}
                    onValueChange={(value) =>
                      handleInputChange("likelihood_id", value)
                    }
                    disabled={submitting || likelihoods.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select likelihood level" />
                    </SelectTrigger>
                    <SelectContent>
                      {likelihoods.map((likelihood) => (
                        <SelectItem key={likelihood.id} value={likelihood.id}>
                          {likelihood.description} (Level {likelihood.value})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                {likelihoods.length === 0 && (
                  <FormMessage>
                    No likelihoods available. Create likelihoods first.
                  </FormMessage>
                )}
              </FormItem>

              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => setShowControlDialog(true)}
                disabled={!risk?.id}
              >
                <Shield className="h-4 w-4 mr-2" />
                Apply Controls
              </Button>
            </Form>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={
                loading ||
                submitting ||
                !formData.name.trim() ||
                !formData.description.trim() ||
                hasNoMetadata
              }
            >
              {submitting && <Spinner className="mr-2 h-4 w-4" />}
              {submitting ? "Updating..." : "Update Risk"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showControlDialog && (
        <ApplyControlToRiskFromRiskView
          open={showControlDialog}
          onClose={() => setShowControlDialog(false)}
          risk={risk}
          organizationId={organizationId}
          businessUnitId={businessUnitId}
          onControlApplied={handleControlApplied}
        />
      )}
    </>
  );
};

export default EditRiskForm;
